import React, { useMemo } from 'react';
import { Box, Button, FormHelperText, Tooltip } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useFormContext, useWatch } from 'react-hook-form';
import { LabeledChip } from '@/components/common';
import { convertFormulaToDisplay } from '@/components/campaigns/FormulaBuilder/formulaUtils';
import { useCampaignQuestionsEditor } from '@/components/campaigns/CampaignQuestionsEditor/context/CampaignQuestionsEditorContext';

import type { FieldError } from 'react-hook-form';
import type { FormulaQuestion, CampaignQuestion } from '../../types';

interface QuestionFormulaFieldProps {
    fieldIndex?: number;
    error?: FieldError;
}

/**
 * Formula field with an icon to open the formula builder modal.
 */
const QuestionFormulaField: React.FC<QuestionFormulaFieldProps> = ({ fieldIndex, error }) => {
    const { openModal } = useCampaignQuestionsEditor();
    const formContext = useFormContext();

    // Watch the current question from form state
    const question = useWatch({
        control: formContext.control,
        name: `questions.${fieldIndex}` as const,
    }) as FormulaQuestion;

    // Watch all questions for formula display conversion
    const allQuestions = (useWatch({
        control: formContext.control,
        name: 'questions',
    }) || []) as CampaignQuestion[];

    const handleOpenModal = () => {
        if (fieldIndex !== undefined) {
            openModal(fieldIndex);
        }
    };

    // Convert UUID-based formula to display format (Q1, Q2, etc.)
    const formulaDisplay = useMemo(() => {
        if (!question?.formula) return null;

        return convertFormulaToDisplay(question.formula, allQuestions);
    }, [question?.formula, allQuestions]);

    const buttonLabel = formulaDisplay ? 'Edit' : 'Set formula';

    return (
        <Box sx={{ flex: 1, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {formulaDisplay && <LabeledChip label="Formula" value={formulaDisplay} sx={{ mt: 0 }} />}
                <Tooltip title="Configure a formula to calculate this question's value based on other questions" arrow>
                    <Button variant="text" size="small" color="primary" onClick={handleOpenModal} startIcon={<Edit fontSize="small" />}>
                        {buttonLabel}
                    </Button>
                </Tooltip>
            </Box>
            {error && <FormHelperText error>{error.message}</FormHelperText>}
        </Box>
    );
};

export default QuestionFormulaField;
